import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PackageCard from "./PackageCard";
import styles from "./PackageSection.module.css";
import { PACKAGE_CONFIG } from "../../utils/packageGenerator";

const PackageSection = ({ packages, currency, onPackageSelect }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRatePlan, setSelectedRatePlan] = useState("standard");
  const [processedPackages, setProcessedPackages] = useState(packages);
  const [availableRatePlans, setAvailableRatePlans] = useState([]);

  // Find common rate plans across all packages
  useEffect(() => {
    if (!packages || packages.length === 0) return;

    // Find rate plans available in ANY package
    const allRatePlans = new Set();
    const ratePlanDetails = {};

    packages.forEach((pkg) => {
      if (pkg.ratePlanOptions) {
        const pkgRatePlans = Object.keys(pkg.ratePlanOptions);
        console.log(
          `[PackageSection] ${pkg.name} has rate plans:`,
          pkgRatePlans
        );

        // Add all rate plans from this package
        pkgRatePlans.forEach((code) => {
          allRatePlans.add(code);
          // Store rate plan details from first package that has it
          if (!ratePlanDetails[code]) {
            ratePlanDetails[code] = pkg.ratePlanOptions[code];
          }
        });
      } else {
        console.log(`[PackageSection] ${pkg.name} has NO ratePlanOptions`);
      }
    });

    const availableRatePlans = Array.from(allRatePlans);
    console.log("[PackageSection] Available rate plans:", availableRatePlans);

    if (availableRatePlans && availableRatePlans.length > 0) {
      // Get rate plan details
      const ratePlanList = availableRatePlans.map((code) => {
        return {
          code,
          name: ratePlanDetails[code].name || code,
          description: getRatePlanDescription(code),
        };
      });

      setAvailableRatePlans(ratePlanList);

      // Set default selected rate plan - prefer one that most packages have
      const defaultRatePlan =
        packages[0]?.selectedRatePlan || availableRatePlans[0] || "standard";
      console.log(
        "[PackageSection] Setting default rate plan:",
        defaultRatePlan
      );
      setSelectedRatePlan(defaultRatePlan);
      updatePackagesWithRatePlan(defaultRatePlan);
    } else {
      setProcessedPackages(packages);
    }
  }, [packages]);

  // Helper function to get rate plan description
  const getRatePlanDescription = (code) => {
    const descriptions = {
      standard: "Free Cancellation",
      "advance-purchase": "Advance Purchase",
      "non-refundable": "Non-Refundable",
      flexible: "Flexible Rate",
      breakfast: "Breakfast Included",
      "half-board": "Breakfast & Lunch/Dinner Included",
      "full-board": "All Meals Included",
      ep: "Room Only",
      cp: "Breakfast Included",
      map: "Breakfast & Lunch/Dinner",
      ap: "All Meals Included",
    };
    return descriptions[code.toLowerCase()] || code;
  };

  // Update all packages with selected rate plan
  const updatePackagesWithRatePlan = (ratePlanCode) => {
    if (!packages) return;

    console.log(`[PackageSection] Switching to rate plan: ${ratePlanCode}`, {
      packages,
    });

    const updated = packages.map((pkg) => {
      if (pkg.ratePlanOptions && pkg.ratePlanOptions[ratePlanCode]) {
        const option = pkg.ratePlanOptions[ratePlanCode];
        console.log(
          `  ✓ ${pkg.name}: ${option.rooms?.length} rooms, ₹${option.totalPrice}`
        );

        return {
          ...pkg,
          rooms: option.rooms,
          totalPrice: option.totalPrice,
          totalDiscount: option.totalDiscount || 0,
          selectedRatePlan: ratePlanCode,
        };
      }

      console.log(
        `  ⚠ ${pkg.name}: Rate plan ${ratePlanCode} not available, keeping current rate plan`
      );
      // Keep the package as-is if it doesn't support this rate plan
      return pkg;
    });

    setProcessedPackages(updated);
  };

  // Handle rate plan change
  const handleRatePlanChange = (ratePlanCode) => {
    console.log(`[PackageSection] User selected rate plan: ${ratePlanCode}`);
    setSelectedRatePlan(ratePlanCode);
    updatePackagesWithRatePlan(ratePlanCode);
  };

  // Check scroll position for arrow visibility
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScroll);
      }
      window.removeEventListener("resize", checkScroll);
    };
  }, [processedPackages]);

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -340, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 340, behavior: "smooth" });
    }
  };

  // Handle package selection
  const handlePackageSelect = async (packageData) => {
    try {
      setIsLoading(true);

      // Validate packageData
      if (
        !packageData ||
        !packageData.rooms ||
        !Array.isArray(packageData.rooms)
      ) {
        console.error("Invalid package data:", packageData);
        return;
      }

      // Convert package rooms to selectedRatePlans format
      const selectedRatePlans = [];

      // Track how many times we've added each room+rateplan combination
      const roomRatePlanCounts = {};

      packageData.rooms.forEach((room, index) => {
        const ratePlanIdx = room.ratePlanIdx !== undefined ? room.ratePlanIdx : 0;
        const baseId = `${room.roomId}_${room.ratePlanCode || 'standard'}_${ratePlanIdx}`;
        
        // Track how many times we've seen this combination
        if (!roomRatePlanCounts[baseId]) {
          roomRatePlanCounts[baseId] = 0;
        }
        const instanceIdx = roomRatePlanCounts[baseId];
        roomRatePlanCounts[baseId]++;

        let rateplanConfig = {};
        try {
          rateplanConfig = room.ratePlan.ratePlanConfig
            ? JSON.parse(room.ratePlan.ratePlanConfig)
            : {};
        } catch (error) {
          console.error("Error parsing rate plan config:", error);
        }

        // Build rpObject from the ratePlan data
        const rpObject = {
          ...rateplanConfig,
        };

        // Create the selected rate plan object
        // Use the same ID for all rooms of same type/rateplan, but different idx
        const selectedRatePlan = {
          sAdult: room.adults || 0,
          sChild: room.children || 0,
          sInfant: room.infants || 0,
          code: room.ratePlan?.ratePlanCode,
          roomId: room.roomId,
          rpObject: rpObject,
          id: baseId,  // Same ID for all instances of same room/rateplan
          idx: instanceIdx,  // Different idx for each instance (0, 1, 2...)
          RoomImages: room.roomImages || [],
          roomName: room.roomName || "Room",
        };

        console.log("PackageSection: Creating selectedRatePlan:", {
          roomId: selectedRatePlan.roomId,
          code: selectedRatePlan.code,
          id: selectedRatePlan.id,
          idx: selectedRatePlan.idx,
          baseId,
          instanceIdx,
          ratePlanIdx,
        });

        selectedRatePlans.push(selectedRatePlan);
      });

      // Dispatch to Redux store
      dispatch({
        type: "SET_SELECTED_PACKAGE",
        payload: {
          package: packageData,
          selectedRatePlans,
        },
      });

      // Call parent handler if provided
      if (onPackageSelect) {
        await onPackageSelect(packageData, selectedRatePlans);
      }
    } catch (error) {
      console.error("Error selecting package:", error);
      console.error("Stack trace:", error.stack);
      // Show user-friendly error message if needed
      if (window.toastr) {
        window.toastr.error("Failed to select package. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }

    // Scroll to rooms section if auto-select is enabled
    if (PACKAGE_CONFIG.AUTO_SELECT) {
      setTimeout(() => {
        const roomsSection = document.getElementById("rooms-section");
        if (roomsSection) {
          roomsSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={styles.packageSection}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingText}>
            Applying package selection...
          </div>
        </div>
      </div>
    );
  }

  // No packages available
  if (!processedPackages || processedPackages.length === 0) {
    return null;
  }

  return (
    <div className={styles.packageSection}>
      {/* Info Banner - Moved to top */}
      {PACKAGE_CONFIG.SHOW_SAVINGS &&
        processedPackages.some((pkg) => pkg.totalDiscount > 0) && (
          <div className={styles.infoBanner}>
            <svg
              className={styles.infoIcon}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
            <span className={styles.infoText}>
              {t(
                "package_info",
                "Packages include special discounts and are optimized for your group size"
              )}
            </span>
          </div>
        )}

      {/* Section Header */}
      <div className={styles.sectionHeader}>
        <div className={styles.headerContent}>
          <h2 className={styles.sectionTitle}>
            {t("recommended_packages", "Recommended Packages")}
          </h2>
        </div>

        {/* Navigation Arrows for Desktop */}
        {processedPackages.length > 2 && (
          <div className={styles.navigationDesktop}>
            <button
              className={`${styles.navButton} ${
                !showLeftArrow ? styles.disabled : ""
              }`}
              onClick={scrollLeft}
              disabled={!showLeftArrow}
              aria-label="Scroll left"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              className={`${styles.navButton} ${
                !showRightArrow ? styles.disabled : ""
              }`}
              onClick={scrollRight}
              disabled={!showRightArrow}
              aria-label="Scroll right"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Global Rate Plan Selector - Disabled for now */}
      {/* {availableRatePlans.length > 1 && (
        <div className={styles.ratePlanTabs}>
          {availableRatePlans.map((ratePlan) => (
            <button
              key={ratePlan.code}
              className={`${styles.ratePlanTab} ${
                selectedRatePlan === ratePlan.code ? styles.active : ""
              }`}
              onClick={() => handleRatePlanChange(ratePlan.code)}
              aria-label={`Select ${ratePlan.description} rate plan`}
            >
              {ratePlan.description}
            </button>
          ))}
        </div>
      )} */}

      {/* Package Cards Container */}
      <div className={styles.packagesContainer}>
        <div className={styles.packagesScroll} ref={scrollContainerRef}>
          {processedPackages.map((pkg, index) => (
            <div key={`${pkg.type}-${index}`} className={styles.packageWrapper}>
              <PackageCard
                packageData={pkg}
                currency={currency}
                onSelect={handlePackageSelect}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PackageSection;
